<i18n>
{
  "en": {
    "noPriceData": "There are no price recommendations for this period",
    "savePricesError": "Error saving prices. Please, try it later.",
    "savePricesSuccess": "Saved prices successfully",
    "recommendationPeriod": "Price recommendations",
    "confirmTitle": "Confirming changes for all products",
    "confirmDescription": "Changes will be saved for all products and groups",
    "confirm": "Confirm",
    "confirmAll": "Confirm all",
    "deconfirmAll": "Deselect all",
    "errorConfirmAll": "Error when confirming all",
    "errorOnCofirm": "Error when confirm",
    "generatingPrices": "Recommendations are being generated",
    "priceProfitabilityError": "Error loading prices profitability. Please, try it later",
    "groupIndexingError": "Error loading group indexing rules.",
    "productListError": "Error loading product list",
    "priceGroupFilter": "Filter by price group",
    "selectLanguage": "Select the language to display",
    "showIndexedPrices": "Show indexed prices",
    "indexedPricesFunctionalityHelp": "This functionality is available with one service periods by now"
  },
  "es": {
    "noPriceData": "No hay recomendaciones de precio para este periodo",
    "savePricesError": "Error guardando precios. Inténtelo de nuevo más tarde.",
    "savePricesSuccess": "Se han guardado los precios correctamente",
    "recommendationPeriod": "Recomendaciones de precios",
    "confirmTitle": "Confirmando precios para todos los productos",
    "confirmDescription": "Se guardarán los cambios de precio para todos los productos y agrupaciones",
    "confirm": "Confirmar",
    "confirmAll": "Confirmar todos",
    "deconfirmAll": "Desconfirmar todos",
    "errorConfirmAll":  "Error al confirmar todos",
    "errorOnCofirm": "Error al confirmar",
    "generatingPrices": "Las recomendaciones se están generando",
    "priceProfitabilityError": "Error cargando previsión de rentabilidad en precios. Inténtelo de nuevo más tarde.",
    "groupIndexingError": "Error cargando las reglas de indexación de agrupación.",
    "productListError": "Error cargando la lista de productos",
    "priceGroupFilter": "Filtrar por grupo de precios",
    "selectLanguage":  "Seleccionar el lenguaje para mostrar",
    "showIndexedPrices": "Mostrar precios indexados",
    "indexedPricesFunctionalityHelp": "Por ahora esta funcionalidad solo esta disponible con periodos de un servicio."
  }
}
</i18n>

<template>
  <v-container>
    <v-flex>
      <v-row>
        <v-col class="d-flex mt-lg-4 mt-md-4" cols="12" sm="6">
          <v-select multiple chips
                    v-model="filterByPriceGroup"
                    :items="groupOfProductPricingList"
                    item-text="name"
                    item-value="guid"
                    :label="$t('priceGroupFilter')"
                    hide-details/>
        </v-col>
        <v-col class="d-flex mt-lg-4 mt-md-4" cols="12" sm="6">
          <v-select v-model="language"
                    :items="pointOfSaleLanguages"
                    item-text="name"
                    item-value="alternative"
                    :label="$t('selectLanguage')"
                    hide-details
                    height="42"
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col class="d-flex flex-column flex-md-row align-center" cols="12" sm="7">
          <div class="text-center font-weight-bold ma-1 text-md-h6 text-body-1">
            {{ $t('recommendationPeriod') }}
          </div>
          <div class="text-center font-weight-bold ma-1 text-md-h6 text-body-1" v-if="selectedPeriod">
            {{ selectedPeriod.name }}
          </div>
        </v-col>
        <v-col class="d-flex align-center justify-center" cols="12" sm="3">
          <v-switch :disabled="!useOneService"
                    v-model="showIndexedPrices" class="mr-0 pb-1"
          ></v-switch>
          <div class="font-weight-bold ma-1 text-md-h6 text-body-1 text-no-wrap">
            {{ $t('showIndexedPrices') }}
          </div>
        </v-col>
        <v-col class="d-flex align-center justify-center" cols="12" sm="2">
          <group-legend-info-button/>
        </v-col>
      </v-row>
    </v-flex>
    <v-card elevation="3"
      class="mb-3 mb-md-2 mt-2 | d-flex flex-grow-1 | flex-md-row flex-row-reverse | justify-md-start justify-space-between | align-center | checkbox">
      <v-checkbox dense class="align-center justify-center my-0 mr-0 ml-2 pa-0"
                  :input-value="globalRecommendationValue"
                  :disabled="isInterfaceDisabled"
                  hide-details @click="onConfirmAll"/>
      <div class="my-1 ml-md-0 ml-3 font-weight-bold">{{ globalConfirmButtonString }}</div>
    </v-card>
    <dynameat-skeleton v-if="isLoading" skeleton-type="recommendationList" :container="false"/>
    <v-list class="pb-16" v-if="showPricesList">
      <product-price-card v-for="productRecommendation in productRecommendationsFilteredAndSorted"
                          :key="productRecommendation.guid"
                          class="mb-3 mb-md-2"
                          :product-recommendation="productRecommendation"
                          :sale-formats-by-guid="saleFormatsByGuid"
                          :is-confirmation-disabled="!isPeriodConfirmationEditable"
                          :group="priceGroupNameByGuid[priceGroupGuidByProductDict[productRecommendation.guid]]"
                          :showIndexedPrices="showIndexedPrices"
                          @confirmation-changed="onConfirmationChanged"
                          @group-click="onGoToPricesTable"/>
    </v-list>
    <div v-if="emptyPriceList && !isLoading" class="d-flex mt-1 grey--text justify-space-around">
      <span>{{ $t('noPriceData') }}</span>
    </div>
    <dynameat-confirmation-modal :visible="confirmModalVisible"
                                 :title="$t('confirmTitle')"
                                 :description="$t('confirmDescription')"
                                 :confirm-text="$t('confirm')"
                                 :is-loading="isLoading"
                                 @confirm="onSave"
                                 @cancel="onToggleModal"/>

    <dynameat-confirmation-modal :visible="recommendationModalVisible"
                                 :is-loading="isLoading"
                                 :title="$t('generatingPrices')"/>

    <dynameat-confirmation-buttons @cancel="onCancel" @confirm="onToggleModal"
                                   :confirm-enabled="isPeriodConfirmationEditable"/>
  </v-container>
</template>

<script>
import { createErrorNotification, createSuccessNotification } from '@ulibs/unnotificationsqueue';
import DynameatConfirmationButtons from '@dynameat/components/DynameatConfirmationButtons';
import DynameatDropdown from '@dynameat/components/DynameatDropdown';
import { periodMixins } from '@pointOfSale/mixins';
import VueRouter from 'vue-router';
import DynameatSkeleton from '@dynameat/components/DynameatSkeleton';
import Component from 'vue-class-component';
import Vue from 'vue';
import { Prop, Watch } from 'vue-property-decorator';
import GroupLegendInfoButton from '@optimizer/components/GroupLegendInfoButton';
import DynameatConfirmationModal from '@dynameat/components/DynameatConfirmationModal';
import UnFrontChart from '@ulibs/unfrontcharts/UnFrontChart';
import PriceProfitabilityChart from '@optimizer/components/PriceProfitabilityChart.vue';
import { routeOutConfirmationMixin } from '@/apps/core/mixins';
import {
  Components, MapActions, MapGetters, Mixins
} from '@/apps/core/helpers/decorators';
import ProductPriceCard from '../components/ProductPriceCard.vue';

const { isNavigationFailure, NavigationFailureType } = VueRouter;

Component.registerHooks(['beforeRouteLeave']);
@Component
@Components({
  ProductPriceCard,
  PriceProfitabilityChart,
  DynameatSkeleton,
  DynameatDropdown,
  DynameatConfirmationButtons,
  DynameatConfirmationModal,
  GroupLegendInfoButton,
  UnFrontChart
})
@Mixins([periodMixins, routeOutConfirmationMixin])
@MapGetters(['productRecommendationMap', 'isLoading', 'groupList', 'masterSaleFormatList',
  'priceProfitability', 'pointOfSaleSettings', 'boostedProductSet', 'groupIndexingRuleList',
  'groupOfProductPricingList', 'managedProductList', 'pointOfSaleLanguages', 'optimizerLanguage',
  'useOneService'])
@MapActions(['addNotification', 'saveProductPriceRecommendationsRequest', 'setProductPriceRecommendationsConfirmed',
  'setLoading', 'forecastDemandListRequest', 'saveForecastDemandListRequest', 'validatePeriodDemand',
  'validatePeriodPrices', 'predictionPeriodRequest', 'priceProfitabilityRequest',
  'debouncedProfitabilityRequest', 'removeNotificationById', 'groupIndexingRuleListRequest',
  'loadManagedProductListData', 'masterProductListRequest', 'selectPriceLanguage'])
export default class RecommendationList extends Vue {
  @Prop({ Boolean, default: false }) recommendationModalVisible;

  @Prop({ type: String, default: '' }) generationState;

  confirmModalVisible = false;

  filterByPriceGroup = [];

  language = 'alt1'

  showIndexedPrices = false

  get showPricesList() {
    return !this.isLoading && !this.emptyPriceList;
  }

  get globalRecommendationValue() {
    return this.mainProductRecommendations.every((rec) => this.getRecommendationConfirmedValue(rec));
  }

  get globalConfirmButtonString() {
    return this.globalRecommendationValue ? this.$t('deconfirmAll') : this.$t('confirmAll');
  }

  get saleFormatsByGuid() {
    const saleFormats = {};
    this.masterSaleFormatList.forEach((sf) => {
      saleFormats[sf.guid] = sf.title;
    });
    return saleFormats;
  }

  get mainProductRecommendations() {
    return [...this.productRecommendationMap.values()].filter((rec) => !rec.is_subordinate_product);
  }

  get productRecommendationsFiltered() {
    if (this.filterByPriceGroup.length > 0) {
      let productsToShow = new Set();
      for (const [key, value] of Object.entries(this.productsByPriceGroupDict)) {
        if (this.filterByPriceGroup.includes(key)) productsToShow = new Set([...productsToShow, ...value]);
      }
      const productsToShowArray = Array.from(productsToShow);
      return this.mainProductRecommendations.filter((el) => productsToShowArray.includes(el.guid));
    }
    return this.mainProductRecommendations;
  }

  get productRecommendationsFilteredAndSorted() {
    const recommendations = this.productRecommendationsFiltered;

    // Check if there are recommendations to sort
    if (recommendations.length > 0) {
      // Construct the language key based on the selected language
      const languageKey = `nameAlt${parseInt(this.language.slice(-1), 10) || 1}`;

      // Filter out products that don't have the languageKey property
      const validRecommendations = recommendations.filter((product) => product.hasOwnProperty(languageKey));

      // Sort the valid products based on the languageKey
      const sortedRecommendations = validRecommendations.slice().sort((a, b) => {
        const nameA = a[languageKey] ? a[languageKey].toLowerCase() : '';
        const nameB = b[languageKey] ? b[languageKey].toLowerCase() : '';
        return nameA.localeCompare(nameB);
      });

      return sortedRecommendations;
    }

    return recommendations;
  }

  get emptyPriceList() {
    return this.productRecommendationMap.size === 0;
  }

  get isPeriodConfirmationEditable() {
    return !this.emptyPriceList && this.selectedPeriod?.isProductPriceEditable();
  }

  get isInterfaceDisabled() {
    return this.isLoading || !this.isPeriodConfirmationEditable;
  }

  get productsByPriceGroupDict() {
    const dict = {};
    this.groupOfProductPricingList.forEach((group) => {
      const ownProducts = this.managedProductList.filter((product) => product.group_of_product_pricing_guid === group.guid);
      const productsGuid = ownProducts.reduce((acc, product) => {
        if (product.group_of_product_pricing_guid === group.guid) acc.push(product.guid);
        return acc;
      }, []);
      dict[group.guid] = new Set(productsGuid);
    });
    return dict;
  }

  get priceGroupGuidByProductDict() {
    return this.managedProductList.reduce((acc, product) => {
      acc[product.guid] = product.group_of_product_pricing_guid;
      return acc;
    }, {});
  }

  get priceGroupNameByGuid() {
    return this.groupOfProductPricingList.reduce((acc, group) => {
      acc[group.guid] = group.name;
      return acc;
    }, {});
  }

  @Watch('productRecommendationMap')
  onProductPriceListChanged(newList) {
    const hasSomethingChanged = [...newList.values()].some((pr) => pr.hasSomethingChanged());
    this.allowRouteExitWithoutConfirmation = !hasSomethingChanged;
  }

  @Watch('language', { immediate: false })
  onLanguageChanged(newLanguage, oldLanguage) {
    if (newLanguage !== oldLanguage) {
      this.selectPriceLanguage(newLanguage);
    }
  }

  async created() {
    this.nextMonday = new Date();
    this.nextMonday.setDate(this.nextMonday.getDate() + ((1 + 7 - this.nextMonday.getDay()) % 7));
    this.nextSunday = new Date(this.nextMonday);
    this.nextSunday.setDate(this.nextSunday.getDate() + 6);
    this.language = this.optimizerLanguage;
    const hasSomethingChanged = [...this.productRecommendationMap.values()].some((pr) => pr.hasSomethingChanged());
    this.allowRouteExitWithoutConfirmation = !hasSomethingChanged;
    if (!this.priceProfitability.valid) await this.loadPriceProfitability();
    await this.loadGroupIndexingRuleList();
    await this.loadProductData();
  }

  async loadPriceProfitability() {
    try {
      const { periodGuid } = this.$route.params;
      if (periodGuid) await this.priceProfitabilityRequest({ periodGuid });
    } catch (e) {
      this.addNotification(createErrorNotification(this.$t('priceProfitabilityError'), null, 3000));
    }
  }

  async loadGroupIndexingRuleList() {
    try {
      const { pointOfSaleGuid } = this.$route.params;
      await this.groupIndexingRuleListRequest(pointOfSaleGuid);
    } catch (e) {
      this.addNotification(createErrorNotification(this.$t('groupIndexingError'), null, 3000));
    }
  }

  async loadProductData() {
    await this.setLoading(1);
    try {
      await this.masterProductListRequest(this.$route.params.pointOfSaleGuid);
    } catch (e) {
      this.addNotification(createErrorNotification(this.$t('productListError')));
    }
    await this.setLoading(-1);
  }

  async onConfirmAll() {
    await this.updateConfirmedForAllRecommendations(!this.globalRecommendationValue);
    try {
      await this.debouncedProfitabilityRequest(this.$route.params.periodGuid);
    } catch (e) {
      this.addNotification(createErrorNotification(this.$t('errorConfirmAll')));
    }
  }

  async updateConfirmedForAllRecommendations(confirmed) {
    const productRecommendationGuids = [];
    this.mainProductRecommendations.forEach((totalRec) => {
      totalRec.confirmed = confirmed;
      const recommendations = this.getTotalRecommendations(totalRec);
      const recommendationGuids = recommendations.map((rec) => rec.productRecommendation.guid);
      productRecommendationGuids.push(...recommendationGuids);
    });
    await this.setProductPriceRecommendationsConfirmed({ productRecommendationGuids, confirmed });
  }

  getRecommendationConfirmedValue(recommendation) {
    // Gets true if all recommendations for parent and subordinates are confirmed, false otherwise
    const totalRecommendations = this.getTotalRecommendations(recommendation);
    return totalRecommendations.every((rec) => rec.productRecommendation.confirmed);
  }

  getTotalRecommendations(recommendation) {
    // Returns both parent recommendation and subordinate products' recommendations
    let recommendations = recommendation.subordinate_products.map((subordinateGuid) => this.productRecommendationMap.get(subordinateGuid));
    recommendations = [recommendation, ...recommendations];
    recommendations = recommendations.map((productRecommendation) => ({
      productRecommendation,
      saleFormatTitle: this.saleFormatsByGuid[productRecommendation.master_sale_format]
    }));
    return recommendations;
  }

  onGoToPricesTable({ productRecommendationGuid, groupCodeName }) {
    const isPeriodEditable = this.selectedPeriod?.isProductPriceEditable();
    this.allowRouteExitWithoutConfirmation = true;
    const productRecommendation = this.productRecommendationMap.get(productRecommendationGuid);
    const isReferenceGroup = this.groupIndexingRuleList.some((rule) => rule.referenceGroup === groupCodeName && rule.isActive);
    const isGroupIndexed = this.groupIndexingRuleList.some((rule) => rule.changedGroup === groupCodeName && rule.isActive);
    this.$router.push({
      name: 'priceDetailByDemand',
      params: {
        productRecommendation, groupCodeName, isPeriodEditable, isReferenceGroup, isGroupIndexed
      }
    });
  }

  onToggleModal() {
    this.confirmModalVisible = !this.confirmModalVisible;
  }

  async onSave() {
    try {
      await this.setLoading(1);
      await this.saveProductPriceRecommendationsRequest(this.$route.params.periodGuid);
      await this.validatePeriodPrices(this.$route.params.periodGuid);
      if (!this.selectedPeriod.areDemandRecommendationsValidated) {
        await this.validatePeriodDemand(this.$route.params.periodGuid);
      }
      await this.setLoading(-1);
      this.removeNotificationById(`price${this.$route.params.periodGuid}`);
      this.addNotification(createSuccessNotification(this.$t('savePricesSuccess'), null, 5000));
    } catch (err) {
      this.addNotification(createErrorNotification(this.$t('savePricesError')));
    } finally {
      this.allowRouteExitWithoutConfirmation = true;
      await this.$router.push({
        name: 'optimizer',
        params: { periodGuid: this.$route.params.periodGuid }
      });
    }
  }

  onCancel() {
    this.loadPriceProfitability();
    this.$router.push({
      name: 'optimizer',
      params: { periodGuid: this.$route.params.periodGuid }
    }).catch((e) => {
      if (!isNavigationFailure(e, NavigationFailureType.aborted)) {
        Promise.reject(e);
      }
    });
  }

  async onConfirmationChanged() {
    try {
      await this.debouncedProfitabilityRequest(this.$route.params.periodGuid);
    } catch (e) {
      this.addNotification(createErrorNotification(this.$t('errorOnCofirm')));
    }
  }
}
</script>

<style scoped lang="css">
.info-container {
  margin-bottom: -50px;

}

.price-chart {
  margin-top: -15px;
}
</style>
